import { useState, useEffect } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Input, FormControl, FormLabel, Select, Textarea, VStack, HStack, Stack, Box, Text,
    Table, Thead, Tbody, Tr, Th, Td, Badge, Alert, AlertIcon, Divider, useToast, Heading,
    Checkbox, NumberInput, NumberInputField,
} from '@chakra-ui/react';
import { validarVendaParaReclamacaoManual, criarReclamacaoManual } from '../../services/api';
import ProdutoImagem from '../ProdutoImagem';

const STATUS_OPTIONS = [
    ['opened', 'Aberta'],
    ['closed', 'Encerrada'],
];

const RESPONSAVEL_OPTIONS = [
    ['seller', 'Sua vez de responder'],
    ['buyer', 'Aguardando o comprador'],
];

const PROBLEMAS_SUGERIDOS = [
    ['', 'Escolher motivo pronto (opcional)'],
    ['O produto chegou com defeito', 'Produto com defeito'],
    ['O produto chegou avariado no transporte', 'Avariado no transporte'],
    ['Recebi um produto diferente do que comprei', 'Recebi produto diferente'],
    ['O produto não é compatível com o veículo', 'Não é compatível'],
    ['O produto não corresponde ao anúncio', 'Não corresponde ao anúncio'],
    ['Faltou item / peça no pedido', 'Faltou item no pedido'],
    ['Produto não entregue', 'Produto não entregue'],
    ['Atraso na entrega', 'Atraso na entrega'],
    ['Cliente solicitou troca', 'Solicitação de troca'],
    ['Cliente insatisfeito com o produto', 'Insatisfação com o produto'],
    ['Cobrança / valor incorreto', 'Cobrança incorreta'],
];

const RESOLUCOES_SUGERIDAS = [
    ['', 'Escolher resolução pronta (opcional)'],
    ['Resolvido diretamente com o cliente', 'Resolvido com o cliente'],
    ['Reembolso realizado ao cliente', 'Reembolso realizado'],
    ['Produto trocado', 'Produto trocado'],
    ['Reenvio do item correto', 'Reenvio do item correto'],
    ['Cliente desistiu da reclamação', 'Cliente desistiu'],
    ['Encaminhado ao setor responsável', 'Encaminhado ao setor'],
    ['Sem procedência', 'Sem procedência'],
];

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));
const hojeISO = () => new Date().toISOString().slice(0, 10);
const addDiasUteis = (baseISO, dias) => {
    if (!baseISO) return '';
    const d = new Date(baseISO + 'T00:00:00');
    let adicionados = 0;
    while (adicionados < dias) {
        d.setDate(d.getDate() + 1);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) adicionados++;
    }
    return d.toISOString().slice(0, 10);
};

const formInicial = () => ({
    problem: 'O produto chegou com defeito',
    status: 'opened',
    action_responsible: 'seller',
    title: 'Reclamação manual',
    description: '',
    resolution: '',
    due_date: addDiasUteis(hojeISO(), 3),
    date_created: hojeISO(),
});

const NovaReclamacaoManualModal = ({ isOpen, onClose, onCreated }) => {
    const toast = useToast();
    const [step, setStep] = useState(1);
    const [idVendaInput, setIdVendaInput] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [dadosVenda, setDadosVenda] = useState(null);
    const [itensSel, setItensSel] = useState({});
    const [form, setForm] = useState(formInicial());
    const [salvando, setSalvando] = useState(false);
    const [dueDateTouched, setDueDateTouched] = useState(false);

    const resetar = () => {
        setStep(1); setIdVendaInput(''); setDadosVenda(null); setItensSel({}); setDueDateTouched(false);
        setForm(formInicial());
    };

    const toggleItem = (pid) => setItensSel(p => ({ ...p, [pid]: { ...p[pid], selecionado: !p[pid]?.selecionado } }));
    const setQtyItem = (pid, v, max) => {
        const n = Math.max(0, Math.min(Number(v) || 0, max));
        setItensSel(p => ({ ...p, [pid]: { ...p[pid], qty: n } }));
    };

    const fechar = () => { resetar(); onClose && onClose(); };

    useEffect(() => {
        if (dueDateTouched || !form.date_created) return;
        setForm(f => ({ ...f, due_date: addDiasUteis(f.date_created, 3) }));
    }, [form.date_created, dueDateTouched]);

    const buscarVenda = async () => {
        if (!idVendaInput.trim()) {
            toast({ title: 'Informe o ID da venda', status: 'warning', duration: 3000 });
            return;
        }
        setBuscando(true);
        try {
            const resp = await validarVendaParaReclamacaoManual(idVendaInput.trim());
            if (!resp?.elegivel) {
                toast({
                    title: 'Venda não elegível',
                    description: resp?.error || 'Verifique o ID e tente novamente.',
                    status: 'error', duration: 5000, isClosable: true,
                });
                return;
            }
            setDadosVenda(resp);
            const init = {};
            for (const it of resp.itens || []) {
                if (it.quantidade_disponivel > 0) init[it.produto_id] = { selecionado: true, qty: it.quantidade_disponivel };
            }
            setItensSel(init);
            setStep(2);
        } finally { setBuscando(false); }
    };

    const itensParaEnvio = () => Object.entries(itensSel)
        .filter(([, i]) => i?.selecionado && (i?.qty ?? 0) > 0)
        .map(([pid, i]) => ({ produto_id: Number(pid), quantidade: Number(i.qty) }));

    const salvar = async () => {
        if (!form.problem.trim()) {
            toast({ title: 'Descreva o motivo da reclamação', status: 'warning', duration: 3000 });
            return;
        }
        const itens = itensParaEnvio();
        if (itens.length === 0) {
            toast({ title: 'Selecione ao menos 1 item para reclamar', status: 'warning', duration: 3000 });
            return;
        }
        const payload = {
            id_venda: dadosVenda.venda.id,
            problem: form.problem.trim(),
            status: form.status,
            action_responsible: form.action_responsible,
            itens,
        };
        if (form.title) payload.title = form.title;
        if (form.description) payload.description = form.description;
        if (form.resolution) payload.resolution = form.resolution;
        if (form.due_date) payload.due_date = form.due_date;
        if (form.date_created) payload.date_created = form.date_created;

        setSalvando(true);
        try {
            const resp = await criarReclamacaoManual(payload);
            if (resp?.success) {
                toast({ title: 'Reclamação manual criada', status: 'success', duration: 3000 });
                onCreated && onCreated(resp);
                fechar();
            } else {
                toast({
                    title: 'Falha ao criar reclamação',
                    description: resp?.error || 'Erro desconhecido',
                    status: 'error', duration: 6000, isClosable: true,
                });
            }
        } finally { setSalvando(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={fechar} size={{ base: 'full', md: '3xl' }} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Nova reclamação manual (ECOMMERCE / TAGA)</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {step === 1 && (
                        <VStack spacing={4} align="stretch">
                            <Alert status="info" borderRadius="md">
                                <AlertIcon />
                                <Text fontSize="sm">
                                    Informe a referência da venda (ECOMMERCE ou TAGA). Cliente e itens serão carregados automaticamente. Esta reclamação não gera nenhuma ação no Mercado Livre.
                                </Text>
                            </Alert>
                            <FormControl isRequired>
                                <FormLabel>ID da venda</FormLabel>
                                <HStack>
                                    <Input
                                        value={idVendaInput}
                                        onChange={(e) => setIdVendaInput(e.target.value)}
                                        placeholder="Ex: 17205, ORD-12345"
                                        onKeyDown={(e) => { if (e.key === 'Enter') buscarVenda(); }}
                                    />
                                    <Button colorScheme="orange" onClick={buscarVenda} isLoading={buscando} minW="120px">
                                        Buscar
                                    </Button>
                                </HStack>
                            </FormControl>
                        </VStack>
                    )}

                    {step === 2 && dadosVenda && (
                        <VStack spacing={4} align="stretch">
                            <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px">
                                <HStack justify="space-between" wrap="wrap">
                                    <Box>
                                        <Text fontSize="xs" color="gray.500">Venda</Text>
                                        <Text fontWeight="bold">
                                            #{dadosVenda.venda.id}
                                            <Badge colorScheme="purple" ml={2}>{dadosVenda.venda.origem}</Badge>
                                            {dadosVenda.venda.id_origem && <Text as="span" fontSize="xs" color="gray.600" ml={2}>{dadosVenda.venda.id_origem}</Text>}
                                        </Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500">Cliente</Text>
                                        <Text fontSize="sm">{`${dadosVenda.cliente?.first_name || ''} ${dadosVenda.cliente?.last_name || ''}`.trim() || 'N/I'}</Text>
                                        <Text fontSize="xs" color="gray.600">{dadosVenda.cliente?.doc_number || ''}</Text>
                                    </Box>
                                    <Box>
                                        <Text fontSize="xs" color="gray.500">Valor</Text>
                                        <Text fontWeight="bold">{formatBRL(dadosVenda.venda.valor_total)}</Text>
                                    </Box>
                                </HStack>
                                {dadosVenda.reclamacoes_existentes?.length > 0 && (
                                    <Alert status="warning" mt={2} borderRadius="md" fontSize="xs">
                                        <AlertIcon />
                                        Esta venda já possui {dadosVenda.reclamacoes_existentes.length} reclamação(ões) manual(is).
                                    </Alert>
                                )}
                            </Box>

                            {dadosVenda.itens?.length > 0 && (
                                <Box>
                                    <Heading size="sm" mb={2}>Itens a reclamar</Heading>
                                    <Box overflowX="auto" borderWidth="1px" borderRadius="md">
                                        <Table size="sm" variant="simple">
                                            <Thead>
                                                <Tr>
                                                    <Th></Th>
                                                    <Th>Produto</Th>
                                                    <Th isNumeric>Preço un.</Th>
                                                    <Th isNumeric>Qtd.</Th>
                                                    <Th isNumeric>Já reclam.</Th>
                                                    <Th isNumeric>Reclamar</Th>
                                                </Tr>
                                            </Thead>
                                            <Tbody>
                                                {dadosVenda.itens.map((it) => {
                                                    const sel = itensSel[it.produto_id] || {};
                                                    const disp = it.quantidade_disponivel ?? it.quantidade;
                                                    return (
                                                        <Tr key={it.produto_id} opacity={disp <= 0 ? 0.5 : 1}>
                                                            <Td>
                                                                <Checkbox
                                                                    isDisabled={disp <= 0}
                                                                    isChecked={!!sel.selecionado}
                                                                    onChange={() => toggleItem(it.produto_id)}
                                                                    colorScheme="orange"
                                                                />
                                                            </Td>
                                                            <Td minW={{ base: '180px', md: '240px' }}>
                                                                <HStack align="flex-start" spacing={2}>
                                                                    <Box flexShrink={0}>
                                                                        <ProdutoImagem produto={it} width="44px" height="44px" />
                                                                    </Box>
                                                                    <Box>
                                                                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>{it.nome}</Text>
                                                                        <Text fontSize="10px" color="gray.500">SKU: {it.sku} · Cód: {it.codigo_global}</Text>
                                                                        <HStack spacing={2} mt="2px" flexWrap="wrap">
                                                                            {it.fornecedor && <Badge colorScheme="blue" fontSize="9px">{it.fornecedor}</Badge>}
                                                                            {it.nome_fabricante && <Badge colorScheme="gray" fontSize="9px">{it.nome_fabricante}</Badge>}
                                                                        </HStack>
                                                                        {disp <= 0 && <Badge colorScheme="red" fontSize="9px" mt="2px">Sem saldo</Badge>}
                                                                    </Box>
                                                                </HStack>
                                                            </Td>
                                                            <Td isNumeric>{formatBRL(it.preco_unitario)}</Td>
                                                            <Td isNumeric>{it.quantidade_total ?? it.quantidade}</Td>
                                                            <Td isNumeric>{it.quantidade_reclamada ?? 0}</Td>
                                                            <Td isNumeric>
                                                                <NumberInput
                                                                    size="xs" min={0} max={disp} step={1}
                                                                    value={sel.qty ?? disp}
                                                                    onChange={(v) => setQtyItem(it.produto_id, v, disp)}
                                                                    isDisabled={!sel.selecionado || disp <= 0}
                                                                    maxW="70px" ml="auto"
                                                                >
                                                                    <NumberInputField textAlign="right" />
                                                                </NumberInput>
                                                            </Td>
                                                        </Tr>
                                                    );
                                                })}
                                            </Tbody>
                                        </Table>
                                    </Box>
                                    <Text fontSize="10px" color="gray.500" mt={1}>
                                        "Já reclam." considera outras reclamações manuais desta venda. Só é possível reclamar o saldo disponível.
                                    </Text>
                                </Box>
                            )}

                            <Divider />

                            <Heading size="sm">Detalhes da reclamação</Heading>

                            <FormControl isRequired>
                                <FormLabel>Motivo</FormLabel>
                                <Select
                                    mb={2} size="sm" value=""
                                    onChange={(e) => { if (e.target.value) setForm({ ...form, problem: e.target.value }); }}
                                >
                                    {PROBLEMAS_SUGERIDOS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                                </Select>
                                <Textarea
                                    value={form.problem}
                                    onChange={(e) => setForm({ ...form, problem: e.target.value })}
                                    placeholder="Ex: Produto chegou com defeito"
                                    rows={2}
                                />
                            </FormControl>

                            <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel>Status</FormLabel>
                                    <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                                        {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel>Responsável / etapa</FormLabel>
                                    <Select value={form.action_responsible} onChange={(e) => setForm({ ...form, action_responsible: e.target.value })}>
                                        {RESPONSAVEL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                    </Select>
                                </FormControl>
                            </Stack>

                            <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align="stretch">
                                <FormControl>
                                    <FormLabel>Prazo para responder</FormLabel>
                                    <Input
                                        type="date"
                                        value={form.due_date}
                                        onChange={(e) => { setDueDateTouched(true); setForm({ ...form, due_date: e.target.value }); }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Data de abertura</FormLabel>
                                    <Input
                                        type="date"
                                        value={form.date_created}
                                        onChange={(e) => setForm({ ...form, date_created: e.target.value })}
                                    />
                                </FormControl>
                            </Stack>

                            <FormControl>
                                <FormLabel>Título (opcional)</FormLabel>
                                <Input
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder="Ex: Reclamação de produto com defeito"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Descrição (opcional)</FormLabel>
                                <Textarea
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    rows={2}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Resolução (opcional)</FormLabel>
                                <Select
                                    mb={2} size="sm" value=""
                                    onChange={(e) => { if (e.target.value) setForm({ ...form, resolution: e.target.value }); }}
                                >
                                    {RESOLUCOES_SUGERIDAS.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
                                </Select>
                                <Textarea
                                    value={form.resolution}
                                    onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                                    rows={2}
                                    placeholder="Preencha se a reclamação já foi resolvida"
                                />
                            </FormControl>
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter>
                    {step === 2 && (
                        <Button variant="ghost" mr="auto" onClick={() => setStep(1)}>Voltar</Button>
                    )}
                    <Button variant="outline" mr={2} onClick={fechar}>Cancelar</Button>
                    {step === 1 && (
                        <Button colorScheme="orange" onClick={buscarVenda} isLoading={buscando}>Continuar</Button>
                    )}
                    {step === 2 && (
                        <Button colorScheme="orange" onClick={salvar} isLoading={salvando}>Cadastrar reclamação</Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default NovaReclamacaoManualModal;
